import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag, Calendar, Check,
  Gem, Shield, Truck, Award, Heart, Share2, Sparkles, ArrowRight,
  Sparkle, BadgeCheck, PackageCheck, Clock, MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import ProductCard from '@/components/ProductCard';
import { getSizeOptions } from '@/lib/argentoSizes';
import { SizeGuide } from '@/components/SizeGuide';
import { whatsappProductLink } from '@/lib/whatsapp';
import ProductGallery from '@/components/ProductGallery';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useScrollAnimation } from '@/hooks/useAnimations';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { toast } from 'sonner';
import axios from 'axios';
import { KlarnaBadge } from '@/components/KlarnaBadge';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState('default');
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const valuesSection = useScrollAnimation(0.2);
  const suggestedSection = useScrollAnimation(0.15);
  const suggestedScrollRef = useRef(null);

  const scrollSuggested = (dir) => {
    const el = suggestedScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  // Costruisco la lista completa di scelte: variante "default" + tutte le varianti salvate
  const variantOptions = useMemo(() => {
    if (!product) return [];
    const base = {
      id: 'default',
      material: product.material,
      price: product.price,
      image_url: product.image_url,
      in_stock: product.in_stock,
      isDefault: true,
    };
    const extras = (product.variants || []).map((v) => ({
      id: v.id,
      material: v.material,
      price: v.price != null && v.price !== '' ? Number(v.price) : product.price,
      image_url: v.image_url || product.image_url,
      in_stock: v.in_stock !== false,
      sku: v.sku,
      isDefault: false,
    }));
    return [base, ...extras];
  }, [product]);

  const selectedVariant = useMemo(
    () => variantOptions.find((v) => v.id === selectedVariantId) || variantOptions[0],
    [variantOptions, selectedVariantId]
  );

  // Quando arriva il prodotto, resetto la selezione
  useEffect(() => {
    if (product) setSelectedVariantId('default');
  }, [product]);

  const displayPrice = selectedVariant?.price ?? product?.price;
  const displayMaterial = selectedVariant?.material || product?.material;
  const displayInStock = selectedVariant?.in_stock ?? product?.in_stock;
  const isWatch = product?.category === 'Orologi';

  // Galleria: immagini prodotto + immagine variante in testa (se selezionata)
  const galleryImages = useMemo(() => {
    const base = product?.images?.length
      ? product.images
      : product?.image_url
        ? [product.image_url]
        : [];
    if (selectedVariant && !selectedVariant.isDefault && selectedVariant.image_url) {
      return [selectedVariant.image_url, ...base.filter((u) => u !== selectedVariant.image_url)];
    }
    return base;
  }, [product, selectedVariant]);

  const saved = product ? isInWishlist(product.id) : false;

  const handleToggleWishlist = () => {
    if (!product) return;
    const added = toggleWishlist(product.id);
    toast.success(added ? 'Aggiunto ai preferiti' : 'Rimosso dai preferiti', {
      description: product.name,
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: product?.name,
      text: `${product?.name} — Ciro Gallotti`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copiato negli appunti');
      }
    } catch {
      /* utente ha annullato la condivisione */
    }
  };

  useSeo({
    title: product ? `${product.name} — ${product.material}` : 'Prodotto',
    description: product
      ? `${product.name} in ${product.material}. ${(product.description || '').slice(0, 140)} Disponibile online o nello showroom di Ciro Gallotti a Napoli.`
      : 'Dettaglio gioiello della collezione Ciro Gallotti, gioielleria artigianale di Napoli.',
    path: `/prodotto/${id}`,
    image: product?.image_url,
    type: 'product',
    jsonLd: product
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            '@id': `${SITE_URL}/prodotto/${product.id}`,
            name: product.name,
            description: product.description,
            image: product.image_url,
            sku: product.id,
            category: product.category,
            material: product.material,
            brand: { '@type': 'Brand', name: 'Ciro Gallotti' },
            offers: product.price_on_request ? undefined : {
              '@type': 'Offer',
              url: `${SITE_URL}/prodotto/${product.id}`,
              priceCurrency: 'EUR',
              price: product.price,
              availability: product.in_stock
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@type': 'Organization', name: 'Ciro Gallotti' },
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Collezione', item: `${SITE_URL}/collezione` },
              { '@type': 'ListItem', position: 3, name: product.name, item: `${SITE_URL}/prodotto/${product.id}` },
            ],
          },
        ]
      : null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productRes = await axios.get(`${API}/products/${id}`);
        const current = productRes.data;
        setProduct(current);

        // Suggeriti: prima stessa categoria, poi qualunque (fino a 4)
        const allRes = await axios.get(`${API}/products`);
        const coll = (p) => p?.collection || 'Generale';
        const all = (allRes.data || []).filter((p) => p.id !== id && coll(p) === coll(current));
        const sameCat = all.filter((p) => p.category === current?.category);
        const others = all.filter((p) => p.category !== current?.category);
        const combined = [...sameCat, ...others].slice(0, 12);
        setSuggested(combined);
      } catch (e) {
        console.error('Error fetching product:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatPrice = (price) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);

  const handleAddToCart = async () => {
    if (product.price_on_request) {
      window.open(whatsappProductLink(product), '_blank', 'noopener');
      return;
    }
    if (sizeOptions.length && !selectedSize) {
      setSizeError(true);
      toast.error('Seleziona una misura');
      document.querySelector('[data-testid="size-selector"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setAdding(true);
    const success = await addToCart(product.id, quantity, selectedSize);
    if (success) {
      const variantNote = [
        selectedVariant && !selectedVariant.isDefault ? selectedVariant.material : null,
        selectedSize,
      ].filter(Boolean).join(', ');
      toast.success('Prodotto aggiunto al carrello', {
        description: `${quantity}× ${product.name}${variantNote ? ` (${variantNote})` : ''}`,
      });
    } else {
      toast.error("Errore nell'aggiungere al carrello");
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="product-loading">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="aspect-[3/4] bg-line rounded-2xl animate-pulse" />
            <div className="space-y-6">
              <div className="h-3 bg-line w-24 animate-pulse" />
              <div className="h-10 bg-line w-64 animate-pulse" />
              <div className="h-7 bg-line w-32 animate-pulse" />
              <div className="h-28 bg-line animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-ivory pt-24 md:pt-28 min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-display text-h2 text-ink mb-6">Prodotto non trovato</h1>
          <Link to="/collezione">
            <Button>Torna alla Collezione</Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasVariants = variantOptions.length > 1;
  const sizeOptions = getSizeOptions(product);

  const backLink = {
    Argento: { to: '/argento', label: 'Torna alla Collezione Argento' },
    Accessori: { to: '/accessori', label: 'Torna agli Accessori' },
  }[product.collection] || { to: '/collezione', label: 'Torna alla Collezione' };

  return (
    <div className="bg-ivory pt-24 md:pt-28 pb-24 lg:pb-0" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 pt-6">
        <Link
          to={backLink.to}
          className="inline-flex items-center gap-2 text-eyebrow text-muted hover:text-bordeaux transition-colors duration-hover group"
          data-testid="back-to-catalog"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-hover" />
          {backLink.label}
        </Link>
      </div>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Image */}
          <div className="lg:col-span-7 lg:sticky lg:top-28 lg:self-start">
            <ProductGallery
              images={galleryImages}
              alt={`${product.name} — ${displayMaterial} — ${product.category}`}
            />
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="space-y-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1.5 border border-bordeaux text-bordeaux text-eyebrow rounded-full">
                  {product.category}
                </span>
                {product.brand && (
                  <span className="px-3 py-1.5 bg-ink text-ivory text-eyebrow rounded-full">
                    {product.brand}
                  </span>
                )}
                <span className="px-3 py-1.5 border border-line text-muted text-eyebrow rounded-full">
                  {displayMaterial}
                </span>
                {(product.gemstones || []).map((g, i) => (
                  <span
                    key={`${g.type}-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-bordeaux/5 border border-bordeaux/20 text-bordeaux text-eyebrow rounded-full"
                  >
                    <Gem className="w-3 h-3" />
                    {g.type}{g.carats != null ? ` · ${g.carats}ct` : ''}{g.color ? ` · ${g.color}` : ''}{g.clarity ? ` · ${g.clarity}` : ''}
                  </span>
                ))}
              </div>

              <h1
                className="font-display text-h1 text-ink leading-tight"
                data-testid="product-name"
              >
                {product.name}
              </h1>
              {product.model_code && (
                <p className="mt-1 text-eyebrow text-muted" data-testid="product-model-code">
                  Modello {product.model_code}
                </p>
              )}

              <div className="pb-6 hairline-b">
                <div className="flex items-baseline gap-4">
                  <p className="font-display text-h2 text-ink" data-testid="product-price">
                    {product.price_on_request ? 'Prezzo su richiesta' : formatPrice(displayPrice)}
                  </p>
                  {!product.price_on_request && <span className="text-small text-muted">IVA inclusa</span>}
                </div>
                {!product.price_on_request && <KlarnaBadge amount={displayPrice} className="mt-3" />}
              </div>

              <p
                className="text-base text-ink/75 leading-relaxed max-w-reading"
                data-testid="product-description"
              >
                {product.description}
              </p>

              {/* VARIANTI */}
              {hasVariants && (
                <div className="space-y-4" data-testid="variant-selector">
                  <div className="flex items-baseline justify-between">
                    <span className="text-eyebrow text-ink">Materiale</span>
                    <span className="text-small text-muted">{displayMaterial}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {variantOptions.map((v) => {
                      const isActive = v.id === selectedVariantId;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setSelectedVariantId(v.id)}
                          disabled={!v.in_stock}
                          className={`group flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-full border transition-all duration-hover ease-out ${
                            isActive
                              ? 'border-bordeaux bg-bordeaux/5 ring-2 ring-bordeaux/30'
                              : 'border-line hover:border-ink'
                          } ${!v.in_stock ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`variant-option-${v.id}`}
                          aria-pressed={isActive}
                          aria-label={`Seleziona ${v.material}`}
                        >
                          <span className="w-9 h-9 rounded-full overflow-hidden bg-secondary flex-shrink-0">
                            {v.image_url && (
                              <img src={v.image_url} alt="" className="w-full h-full object-cover" />
                            )}
                          </span>
                          <span className={`text-sm ${isActive ? 'text-ink font-medium' : 'text-ink/70'}`}>
                            {v.material}
                          </span>
                          {!v.in_stock && (
                            <span className="text-eyebrow text-muted">Esaurito</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* MISURA (Collezione Argento) */}
              {sizeOptions.length > 0 && (
                <div className="space-y-4" data-testid="size-selector">
                  <div className="flex items-baseline justify-between">
                    <span className="text-eyebrow text-ink">
                      {{ Collane: 'Lunghezza', Bracciali: 'Misura polso', Anelli: 'Taglia anello' }[product.category]}
                    </span>
                    <span className={`text-small ${sizeError ? 'text-bordeaux' : 'text-muted'}`} data-testid="size-selected-label">
                      {selectedSize ? (product.category === 'Anelli' ? `Taglia ${selectedSize}` : selectedSize) : 'Seleziona una misura'}
                    </span>
                  </div>
                  {['Bracciali', 'Anelli'].includes(product.category) && <SizeGuide category={product.category} />}
                  <div className="flex flex-wrap gap-2.5">
                    {sizeOptions.map((s) => {
                      const active = s === selectedSize;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => { setSelectedSize(s); setSizeError(false); }}
                          className={`min-w-[4.25rem] h-11 px-4 rounded-full border text-sm tabular-nums transition-colors duration-hover ${
                            active
                              ? 'border-bordeaux bg-bordeaux text-ivory'
                              : sizeError
                                ? 'border-bordeaux/50 text-ink hover:border-bordeaux'
                                : 'border-line text-ink/80 hover:border-ink'
                          }`}
                          aria-pressed={active}
                          data-testid={`size-option-${s.replace(' ', '')}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className={`flex items-center gap-6 pt-2 ${product.price_on_request ? 'hidden' : ''}`}>
                <span className="text-eyebrow text-muted">Quantità</span>
                <div className="flex items-center border border-line rounded-full">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-11 h-11 flex items-center justify-center text-ink hover:bg-secondary rounded-full transition-colors duration-hover"
                    data-testid="quantity-decrease"
                    aria-label="Diminuisci quantità"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    className="w-12 text-center text-base font-medium tabular-nums"
                    data-testid="quantity-value"
                  >
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-11 h-11 flex items-center justify-center text-ink hover:bg-secondary rounded-full transition-colors duration-hover"
                    data-testid="quantity-increase"
                    aria-label="Aumenta quantità"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  onClick={handleAddToCart}
                  disabled={adding}
                  size="xl"
                  variant="accent"
                  className="w-full"
                  data-testid="add-to-cart-btn"
                >
                  {adding ? (
                    <span className="flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-ivory/30 border-t-ivory rounded-full animate-spin" />
                      Aggiungendo...
                    </span>
                  ) : product.price_on_request ? (
                    <span className="flex items-center gap-3">
                      <MessageCircle className="w-4 h-4" />
                      Chiedi il prezzo su WhatsApp
                    </span>
                  ) : !displayInStock ? (
                    <span className="flex items-center gap-3">
                      <ShoppingBag className="w-4 h-4" />
                      Ordina su Misura
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      <ShoppingBag className="w-4 h-4" />
                      Aggiungi al Carrello
                    </span>
                  )}
                </Button>

                {!displayInStock && (
                  <div
                    className="flex items-start gap-2.5 rounded-xl border border-bordeaux/20 bg-bordeaux/5 px-4 py-3"
                    data-testid="made-to-order-badge"
                  >
                    <Clock className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-ink/80 leading-relaxed">
                      <span className="font-medium text-ink">Disponibile su ordinazione.</span>{' '}
                      Realizzazione artigianale in <span className="font-medium">7–15 giorni</span> lavorativi.
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <Link to="/appuntamento" className="block">
                    <Button variant="outline" className="w-full" data-testid="book-appointment-btn">
                      <Calendar className="w-4 h-4 mr-2" />
                      Prenota Appuntamento
                    </Button>
                  </Link>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={handleToggleWishlist}
                      className={`w-full ${saved ? 'border-bordeaux bg-bordeaux/5 text-bordeaux hover:bg-bordeaux hover:text-ivory' : ''}`}
                      aria-label={saved ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                      aria-pressed={saved}
                      data-testid="wishlist-toggle-btn"
                    >
                      <Heart className="w-4 h-4 mr-2" fill={saved ? 'currentColor' : 'none'} />
                      Preferiti
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleShare}
                      className="w-full"
                      aria-label="Condividi"
                      data-testid="share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Condividi
                    </Button>
                  </div>
                </div>
              </div>

              {/* Trust line */}
              <div className="flex items-center gap-6 text-small text-muted hairline-t pt-6">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-bordeaux" />
                  {displayInStock ? 'Disponibile' : 'Su ordinazione'}
                </span>
                <span className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-bordeaux" />
                  Spedizione 2-3 giorni
                </span>
              </div>

              {/* Dettagli & Specifiche */}
              <div className="pt-2" data-testid="product-specs">
                <Accordion type="single" collapsible defaultValue="dettagli">
                  <AccordionItem value="dettagli" className="border-line">
                    <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
                      <span className="flex items-center gap-3">
                        <Gem className="w-4 h-4 text-bordeaux" /> {isWatch ? "Dettagli dell'Orologio" : 'Dettagli del Gioiello'}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 pb-4 text-sm">
                        <div className="flex flex-col">
                          <dt className="text-muted text-eyebrow">Categoria</dt>
                          <dd className="text-ink">{product.category}</dd>
                        </div>
                        <div className="flex flex-col">
                          <dt className="text-muted text-eyebrow">Materiale</dt>
                          <dd className="text-ink">{displayMaterial}</dd>
                        </div>
                        {product.brand && (
                          <div className="flex flex-col">
                            <dt className="text-muted text-eyebrow">Brand</dt>
                            <dd className="text-ink">{product.brand}</dd>
                          </div>
                        )}
                        {selectedVariant?.sku && (
                          <div className="flex flex-col">
                            <dt className="text-muted text-eyebrow">Codice (SKU)</dt>
                            <dd className="text-ink">{selectedVariant.sku}</dd>
                          </div>
                        )}
                        {!isWatch && (
                          <div className="flex flex-col">
                            <dt className="text-muted text-eyebrow">Lavorazione</dt>
                            <dd className="text-ink">Artigianale</dd>
                          </div>
                        )}
                        {!isWatch && (
                          <div className="flex flex-col">
                            <dt className="text-muted text-eyebrow">Origine</dt>
                            <dd className="text-ink">Napoli, Italia</dd>
                          </div>
                        )}
                        {product.weight_grams != null && (
                          <div className="flex flex-col">
                            <dt className="text-muted text-eyebrow">Peso</dt>
                            <dd className="text-ink">{product.weight_grams} g</dd>
                          </div>
                        )}
                        {(product.gemstones || []).map((g, i) => (
                          <div className="flex flex-col" key={`${g.type}-${i}`}>
                            <dt className="text-muted text-eyebrow">{g.type}</dt>
                            <dd className="text-ink">
                              {[
                                g.carats != null ? `${g.carats} ct` : null,
                                g.color ? `Colore ${g.color}` : null,
                                g.clarity || null,
                                g.cut ? `Taglio ${g.cut}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || 'Presente'}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="cura" className="border-line">
                    <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
                      <span className="flex items-center gap-3">
                        <Sparkle className="w-4 h-4 text-bordeaux" /> Cura & Manutenzione
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pb-4 text-sm text-ink/75">
                        {isWatch ? (
                          <>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Verifica l'impermeabilità: evita l'esposizione all'acqua se non indicata dal costruttore.</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Pulisci cassa e bracciale con panno morbido; evita urti, magneti e sbalzi termici.</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Revisione periodica del movimento e sostituzione batteria/guarnizioni presso il nostro showroom.</li>
                          </>
                        ) : (
                          <>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Conserva il gioiello nella sua custodia, lontano da umidità.</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Pulisci con panno morbido; evita prodotti chimici aggressivi.</li>
                            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Pulizia e lucidatura professionale gratuita a vita in showroom.</li>
                          </>
                        )}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="spedizione" className="border-line">
                    <AccordionTrigger className="text-eyebrow text-ink hover:no-underline hover:text-bordeaux py-4">
                      <span className="flex items-center gap-3">
                        <PackageCheck className="w-4 h-4 text-bordeaux" /> Spedizione & Reso
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pb-4 text-sm text-ink/75">
                        <li className="flex items-start gap-2"><Truck className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Spedizione gratuita e assicurata in tutta Italia (2-3 giorni).</li>
                        <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Reso entro 14 giorni come da Codice del Consumo.</li>
                        <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" /> Confezione regalo premium e certificato di autenticità inclusi.</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============== VALORI / GARANZIE — full-width ============== */}
      <section
        className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-secondary/30 hairline-t hairline-b"
        data-testid="product-values"
      >
        <div className="max-w-container mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <p className="text-eyebrow text-bordeaux mb-4">Perché Sceglierci</p>
            <h2 className="font-display text-h2 text-ink leading-tight">
              I Valori di un <span className="italic">Gioiello Gallotti</span>
            </h2>
            <p className="mt-5 text-ink/65 leading-relaxed">
              Ogni creazione è il risultato di oltre cinquant'anni di maestria orafa napoletana,
              materiali pregiati e dettagli curati a mano.
            </p>
          </div>

          <div
            ref={valuesSection.ref}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children ${valuesSection.isVisible ? 'visible' : ''}`}
          >
            {[
              { icon: Gem,       title: 'Materiali Pregiati',   desc: `${displayMaterial} 18kt e pietre certificate.` },
              { icon: Award,     title: 'Maestria Artigianale', desc: 'Realizzato a mano da maestri orafi napoletani.' },
              { icon: Sparkles,  title: 'Design Esclusivo',     desc: 'Creazioni uniche ispirate alla tradizione di Napoli.' },
              { icon: Shield,    title: 'Garanzia a Vita',      desc: 'Certificato di autenticità e assistenza inclusi.' },
            ].map((item) => (
              <div
                key={item.title}
                className="group text-center p-8 md:p-10 rounded-3xl bg-ivory border border-line hover:border-bordeaux/40 hover:-translate-y-1 shadow-sm hover:shadow-lg transition-all duration-enter ease-out"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-bordeaux/10 group-hover:bg-bordeaux flex items-center justify-center transition-colors duration-hover ease-out">
                  <item.icon className="w-7 h-7 text-bordeaux group-hover:text-ivory transition-colors duration-hover" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-h4 text-ink mb-2">{item.title}</h3>
                <p className="text-small text-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Bullet list — promesse rapide */}
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-8 max-w-5xl mx-auto">
            {[
              'Spedizione gratuita assicurata in Italia',
              'Reso entro 14 giorni — Codice del Consumo',
              'Confezione regalo premium inclusa',
              'Pagamento sicuro con Stripe',
              'Consulenza personalizzata in showroom',
              'Servizio di incisione su richiesta',
              'Resizing gratuito per gli anelli',
              'Pulizia e manutenzione a vita',
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-small text-ink/75">
                <Check className="w-4 h-4 text-bordeaux flex-shrink-0 mt-0.5" strokeWidth={2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============== SUGGERITI ============== */}
      {suggested.length > 0 && (
        <section
          className="py-20 md:py-28 px-6 md:px-12 lg:px-16"
          data-testid="product-suggested"
        >
          <div className="max-w-container mx-auto">
            <div
              ref={suggestedSection.ref}
              className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-14 scroll-fade-up ${suggestedSection.isVisible ? 'visible' : ''}`}
            >
              <div>
                <p className="text-eyebrow text-bordeaux mb-4">Potrebbero piacerti</p>
                <h2 className="font-display text-h2 text-ink leading-tight">
                  Articoli <span className="italic">Suggeriti</span>
                </h2>
              </div>
              <div className="flex items-center gap-5">
                {/* Frecce scorrimento (desktop) */}
                <div className="hidden md:flex items-center gap-2">
                  <button
                    onClick={() => scrollSuggested(-1)}
                    className="w-11 h-11 rounded-full border border-line text-ink hover:border-ink hover:bg-ink hover:text-ivory transition-colors duration-hover flex items-center justify-center"
                    data-testid="suggested-prev"
                    aria-label="Articoli precedenti"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => scrollSuggested(1)}
                    className="w-11 h-11 rounded-full border border-line text-ink hover:border-ink hover:bg-ink hover:text-ivory transition-colors duration-hover flex items-center justify-center"
                    data-testid="suggested-next"
                    aria-label="Articoli successivi"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <Link
                  to={backLink.to}
                  className="flex items-center gap-3 text-ink hover:text-bordeaux transition-colors duration-hover line-animation pb-1 w-fit"
                  data-testid="view-all-collection"
                >
                  <span className="text-eyebrow">Vedi Tutti</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Carousel scorrevole */}
            <div
              ref={suggestedScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 pb-2"
              data-testid="suggested-carousel"
            >
              {suggested.map((p) => (
                <div key={p.id} className="flex-shrink-0 w-[260px] md:w-[300px] snap-start">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== STICKY MOBILE ADD-TO-CART BAR ============== */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-line px-4 py-3 flex items-center gap-3"
        data-testid="sticky-add-bar"
      >
        <button
          onClick={handleToggleWishlist}
          className={`w-12 h-12 flex-shrink-0 rounded-full border flex items-center justify-center transition-colors duration-hover ${
            saved ? 'border-bordeaux bg-bordeaux text-ivory' : 'border-line text-ink'
          }`}
          aria-label={saved ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
          aria-pressed={saved}
          data-testid="sticky-wishlist-btn"
        >
          <Heart className="w-5 h-5" fill={saved ? 'currentColor' : 'none'} />
        </button>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-eyebrow text-muted truncate">{product.name}</span>
          <span className="font-display text-lg text-ink">{product.price_on_request ? 'Su richiesta' : formatPrice(displayPrice)}</span>
        </div>
        <Button
          onClick={handleAddToCart}
          disabled={adding}
          variant="accent"
          className="flex-1 ml-auto"
          data-testid="sticky-add-to-cart-btn"
        >
          {adding ? (
            <span className="w-4 h-4 border-2 border-ivory/30 border-t-ivory rounded-full animate-spin" />
          ) : product.price_on_request ? (
            <span className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </span>
          ) : !displayInStock ? (
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Ordina su Misura
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Aggiungi
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}
